import { useEffect, useRef, useState } from "react";
import { EXTERNAL_LINKS } from "routes/paths";
import { getCookie, setCookie } from "services/cookies";
import { COOKIES } from "constants/cookies";
import { isProductionDomain } from "constants/endpoints";

const SESSION_DURATION_MS = 3 * 60 * 60 * 1000; // 3 hours
const SESSION_DURATION_DAYS = SESSION_DURATION_MS / (24 * 60 * 60 * 1000);
const SARDINE_CLIENT_ID_PROD = "7e18fe8f-7889-4f00-932a-f7e411d547bf";
const SARDINE_CLIENT_ID_SANDBOX = "355d766e-f66d-4073-a442-4f0ea57366bc";

function generateSessionKey() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getOrCreateSessionKey() {
  const existingKey = getCookie(COOKIES.X_SESSION);
  const existingTimestamp = getCookie(COOKIES.X_SESSION_TIMESTAMP);
  const now = Date.now();
  const sessionAge = existingTimestamp
    ? now - parseInt(existingTimestamp, 10)
    : SESSION_DURATION_MS;

  if (existingKey && existingTimestamp && sessionAge < SESSION_DURATION_MS) {
    return existingKey;
  }

  const newKey = generateSessionKey();
  setCookie({
    name: COOKIES.X_SESSION,
    value: newKey,
    days: SESSION_DURATION_DAYS
  });
  setCookie({
    name: COOKIES.X_SESSION_TIMESTAMP,
    value: now.toString(),
    days: SESSION_DURATION_DAYS
  });
  return newKey;
}

export const SardineProvider = ({ children }) => {
  const [isLoaded, setLoaded] = useState(false);
  const timeoutIdRef = useRef(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isLoaded) {
      cancelledRef.current = false;
      try {
        const currentCookieSession = getCookie(COOKIES.X_SESSION);

        if (window._raiseSDKInitializedFlag) {
          if (
            currentCookieSession &&
            currentCookieSession === window._raiseSDKSessionKey
          ) {
            setLoaded(true);
            return;
          }
          window._raiseSDKInitializedFlag = false;
        }

        const isProd = isProductionDomain(window.location.hostname);
        const clientIdFromCookie = getCookie(COOKIES.SARDINE_CLIENT_ID);
        const clientId =
          clientIdFromCookie ||
          (isProd ? SARDINE_CLIENT_ID_PROD : SARDINE_CLIENT_ID_SANDBOX);
        const isSandbox = !isProd;

        if (!clientId) {
          setLoaded(true);
          return;
        }

        function initializeRaiseSDK() {
          if (window._raiseSDKInitializedFlag) {
            return true;
          }
          if (window._Raise && typeof window._Raise.initialize === "function") {
            try {
              const sessionKey = getOrCreateSessionKey();
              window._Raise.initialize({
                clientId,
                sessionKey,
                isSandbox
              });
              window._raiseSDKInitializedFlag = true;
              window._raiseSDKSessionKey = sessionKey;
              return true;
            } catch (initError) {
              console.error("Error initializing Raise SDK:", initError);
            }
          }
          return false;
        }

        function waitForRaiseSDK(maxAttempts, attempt = 0) {
          if (cancelledRef.current) return;
          if (initializeRaiseSDK()) {
            return;
          }
          if (attempt >= maxAttempts) {
            console.error(
              "Raise SDK (_Raise) not available after loading script"
            );
            return;
          }
          timeoutIdRef.current = setTimeout(() => {
            waitForRaiseSDK(maxAttempts, attempt + 1);
          }, 100);
        }

        const sdkUrl = EXTERNAL_LINKS.RAISE_RISK_SDK;
        const existingScript = document.querySelector(
          `script[src="${sdkUrl}"]`
        );
        if (existingScript) {
          if (!initializeRaiseSDK()) {
            waitForRaiseSDK(50);
          }
          setLoaded(true);
          return;
        }

        const loader = document.createElement("script");
        loader.async = true;
        loader.src = sdkUrl;
        loader.onload = () => {
          waitForRaiseSDK(50);
        };
        loader.onerror = (error) => {
          console.error(
            "Error loading Raise SDK script from:",
            loader.src,
            error
          );
        };
        const firstScript = document.getElementsByTagName("script")[0];
        if (firstScript?.parentNode) {
          firstScript.parentNode.insertBefore(loader, firstScript);
        } else {
          document.head.appendChild(loader);
        }
        setLoaded(true);
      } catch (e) {
        console.error("Error loading Raise SDK script:", e.message);
        setLoaded(true);
      }
    }

    return () => {
      cancelledRef.current = true;
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [isLoaded]);

  return <>{children}</>;
};
